/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { Modal, ModalBody, ModalClose, ModalContent, ModalFooter, ModalHeader, ModalTitle, ModalTrigger } from "../ui/Modal";
import Button from "../ui/Button";
import ButtonGroup from "../ui/ButtonGroup";
import Text from "../ui/Text";
import { useQueryClient } from "@tanstack/react-query";
import { useCancelWaitingListItem } from "../../lib/react-query/useWaitingList";
import { queryKeys } from "../../lib/react-query/queryKeys";
import { useToast } from "../ui/Toast";
import { extractErrorMessage } from "../../utils/api/apiErrors";
import { WAITING_LIST_STATUS } from "../../constants/constants";
import styled from "styled-components";

const STALE_ENTRY_MESSAGES = {
  [WAITING_LIST_STATUS.CANCELLED]: "This waiting list entry has already been cancelled",
  [WAITING_LIST_STATUS.BOOKED]: "You have already booked a slot from this waiting list entry",
};

const StyledModalBody = styled(ModalBody)`
  text-align: left;
`;

const WaitingListCancelModal = ({ entryId, trigger }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useCancelWaitingListItem(entryId);

  const handleConfirm = () => {
    mutate(undefined, {
      onSuccess: () => {
        setIsModalOpen(false);
        toast({ description: "You have been removed from the waiting list", variant: "success" });
      },
      onError: (error) => {
        const status = error?.response?.data?.data?.status;
        setError(STALE_ENTRY_MESSAGES[status] || extractErrorMessage(error, "Something went wrong"));
      },
    });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (!error) return;

    setError(null);
    queryClient.invalidateQueries({ queryKey: queryKeys.waitingList.all });
  };

  return (
    <Modal isOpen={isModalOpen} onOpen={() => setIsModalOpen(true)} onClose={handleModalClose}>
      <ModalTrigger>
        {trigger}
      </ModalTrigger>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Cancel waiting list entry</ModalTitle>
        </ModalHeader>
        <StyledModalBody>
          {error ? (
            <Text color="dark">{error}</Text>
          ) : (
            <Text color="dark">This will remove you from the waiting list for this day</Text>
          )}
        </StyledModalBody>
        <ModalFooter>
          {error ? (
            <ButtonGroup itemsWidth="half" justify="end">
              <ModalClose>
                <Button variant="primary" title="Okay" onClick={handleModalClose} />
              </ModalClose>
            </ButtonGroup>
          ) : (
            <ButtonGroup itemsWidth="half">
              <ModalClose>
                <Button variant="outline" title="Close" />
              </ModalClose>
              <Button
                variant="primary"
                title="Cancel entry"
                onClick={handleConfirm}
                loading={isPending}
              />
            </ButtonGroup>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WaitingListCancelModal;
